import React, { useContext, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { AppContext } from '../../App';
import { ITrainingSet } from '../../types/my-types';
import { useTrainingSetUpdate } from '../hooks/useTrainingSetUpdate';
import useGetAllLanguages from '../hooks/languages/useGetAllLanguages';
import SetLanguageDialog from '../common/Dialog/SetLanguageDialog';
import {
  TypographyPropertiesTitleStyled,
  TypographyPropertiesDataStyled,
} from '../common/StyledComponents';
import { AxiosError } from 'axios';

interface ITrainingSetLanguageProps {
  trainingSet: ITrainingSet;
}

const TrainingSetLanguage: React.FC<ITrainingSetLanguageProps> = ({ trainingSet }) => {
  const { setToast } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const queryClient = useQueryClient();
  const { data: languages, isLoading: isLoadingLanguages } = useGetAllLanguages();

  const { mutateAsync: updateTrainingSet, isPending: isUpdating } = useTrainingSetUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSet']
      });
      queryClient.invalidateQueries({
        queryKey: ['trainingSets']
      });
      setToast({ variant: 'success', open: true, message: 'Language updated.' });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: error.message || 'Failed to update language.',
      });
    },
  });

  const handleSubmit = async (languageCode: string | null) => {
    await updateTrainingSet({
      trainingSetId: trainingSet.id,
      data: { language_code: languageCode },
    });
  };

  const languageDisplay = useMemo(() => {
    if (!trainingSet.language_code) return 'Not Assigned';
    if (isLoadingLanguages) return '...';
    const language = languages.find((l) => l.code === trainingSet.language_code);
    return language?.name || '...';
  }, [trainingSet.language_code, languages, isLoadingLanguages]);

  return (
    <>
      <div>
        <TypographyPropertiesTitleStyled>LANGUAGE</TypographyPropertiesTitleStyled>
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => setOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            gap: '4px',
          }}
          data-cy="training-set-language"
        >
          {!trainingSet.language_code && (
            <WarningIcon sx={{ color: 'orange', fontSize: 'small' }} />
          )}
          <TypographyPropertiesDataStyled
            data-cy={trainingSet.language_code ? 'ts-language-code' : 'ts-language-not-assigned'}
            sx={{
              color: trainingSet.language_code ? undefined : '#999',
              fontStyle: trainingSet.language_code ? 'normal' : 'italic',
            }}
          >
            {languageDisplay}
          </TypographyPropertiesDataStyled>
          <EditIcon
            sx={{
              fontSize: '12px',
              color: '#666',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s',
            }}
          />
        </Box>
      </div>

      <SetLanguageDialog
        open={open}
        currentLanguageCode={trainingSet.language_code}
        isUpdating={isUpdating}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default TrainingSetLanguage;
